import React, { useState, useEffect, useMemo } from 'react';
import { getFirestore, collection, getDocs, query, where, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig'; // Ensure this path is correct
import { getAuth, onAuthStateChanged, updateProfile } from 'firebase/auth';
import Select from 'react-select';
import './ManageActivities.css'; // Import custom CSS for styling

function ManageActivities() {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [records, setRecords] = useState([]);
    const [editingRecords, setEditingRecords] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [notification, setNotification] = useState({ show: false, message: '', type: '' });
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedActivityType, setSelectedActivityType] = useState('All');
    const [sortBy, setSortBy] = useState('date');
    const [sortOrder, setSortOrder] = useState('desc');

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const usersCollection = collection(db, 'users');
                const usersSnapshot = await getDocs(usersCollection);
                const usersList = usersSnapshot.docs.map(doc => {
                    const userData = doc.data();
                    // Use DisplayName or displayName, fallback to email if neither exists
                    const displayName = userData.DisplayName || userData.displayName || userData.email || 'Unknown User';
                    return {
                        value: doc.id,
                        label: displayName,
                        email: userData.email || userData.Email,
                        ...userData
                    };
                });
                // Sort users alphabetically by display name
                usersList.sort((a, b) => {
                    const nameA = (a.label || '').toLowerCase();
                    const nameB = (b.label || '').toLowerCase();
                    return nameA.localeCompare(nameB);
                });
                setUsers(usersList);
            } catch (error) {
                console.error("Error fetching users:", error);
                showNotification("Error fetching users", "error");
            }
        };

        fetchUsers();
    }, []);

    const showNotification = (message, type) => {
        setNotification({ show: true, message, type });
        setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
    };

    useEffect(() => {
        if (selectedUser) {
            // Fetch records for the selected user
            const fetchRecords = async () => {
                try {
                    const paths = [
                        'hvac/CallData/Responses',
                        'hvac/ClassData/Responses',
                        'hvac/SkillData/Responses',
                        'hvac/VolunteerData/Responses'
                    ];

                    const recordsPromises = paths.map(async path => {
                        const recordsCollection = collection(db, path);
                        // Get email from selectedUser, handling both email and Email fields
                        const userEmail = (selectedUser.email || selectedUser.Email || '').toLowerCase();
                        const q = query(recordsCollection, where('Email', '==', userEmail));
                        const recordsSnapshot = await getDocs(q);
                        const records = recordsSnapshot.docs.map(doc => ({ id: doc.id, path, ...doc.data() }));
                        return records;
                    });

                    const recordsSnapshots = await Promise.all(recordsPromises);
                    const allRecords = recordsSnapshots.flat();

                    // Log the records to check their structure
                    console.log("Fetched Records:", allRecords);

                    // Sort records by date (default sort, will be overridden by user sorting)
                    allRecords.sort((a, b) => {
                        // Try both 'date' and 'Date' fields
                        const aDate = a.Date || a.date || '';
                        const bDate = b.Date || b.date || '';
                        // Assuming date is stored as YYYYMMDD in Firestore
                        const aNum = aDate.length === 8 && /^\d+$/.test(aDate) ? parseInt(aDate) : 0;
                        const bNum = bDate.length === 8 && /^\d+$/.test(bDate) ? parseInt(bDate) : 0;
                        return bNum - aNum; // Descending by default (newest first)
                    });

                    setRecords(allRecords);
                    setEditingRecords({});
                } catch (error) {
                    console.error("Error fetching records:", error);
                }
            };

            fetchRecords();
        }
    }, [selectedUser]);

    const handleUserChange = (selectedOption) => {
        setSelectedUser(selectedOption);
    };

    const handleEdit = (recordId) => {
        setEditingRecords(prevState => ({ ...prevState, [recordId]: true }));
    };

    const handleSaveEdit = async (recordId, updatedRecord) => {
        setIsLoading(true);
        try {
            const recordRef = doc(db, updatedRecord.path, updatedRecord.id);
            await updateDoc(recordRef, updatedRecord);
            const updatedRecords = records.map(record => record.id === updatedRecord.id ? updatedRecord : record);
            setRecords(updatedRecords);
            setEditingRecords(prevState => ({ ...prevState, [recordId]: false }));
            showNotification("Record updated successfully", "success");
        } catch (error) {
            console.error("Error updating record:", error);
            showNotification("Error updating record", "error");
        }
        setIsLoading(false);
    };

    const handleDelete = async (recordId) => {
        if (!window.confirm('Are you sure you want to delete this record?')) return;
        
        setIsLoading(true);
        try {
            const recordToDelete = records.find(record => record.id === recordId);
            if (recordToDelete) {
                const recordRef = doc(db, recordToDelete.path, recordToDelete.id);
                await deleteDoc(recordRef);
                const updatedRecords = records.filter(record => record.id !== recordId);
                setRecords(updatedRecords);
                showNotification("Record deleted successfully", "success");
            }
        } catch (error) {
            console.error("Error deleting record:", error);
            showNotification("Error deleting record", "error");
        }
        setIsLoading(false);
    };

    const handleInputChange = (recordId, field, value) => {
        const updatedRecords = records.map(record => {
            if (record.id === recordId) {
                return { ...record, [field]: value };
            }
            return record;
        });
        setRecords(updatedRecords);
    };

    // Function to get the activity type from the path
    const getActivityType = (path) => {
        const parts = path.split('/');
        return parts[parts.length - 2];
    };

    // Format date for display
    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        // Try to parse different date formats
        if (dateStr.length === 8 && /^\d+$/.test(dateStr)) {
            // YYYYMMDD format
            const year = dateStr.substring(0, 4);
            const month = dateStr.substring(4, 6);
            const day = dateStr.substring(6, 8);
            return `${month}/${day}/${year}`;
        }
        return dateStr;
    };

    // Get activity type display name
    const getActivityTypeName = (type) => {
        const names = {
            'CallData': 'Calls',
            'ClassData': 'Classes',
            'SkillData': 'Skills',
            'VolunteerData': 'Volunteer'
        };
        return names[type] || type;
    };

    // Get all unique activity types
    const activityTypes = useMemo(() => {
        const types = Array.from(new Set(records.map(record => getActivityType(record.path))));
        return ['All', ...types];
    }, [records]);

    // Filter and sort records
    const filteredAndSortedRecords = useMemo(() => {
        let filtered = records;

        // Filter by activity type
        if (selectedActivityType !== 'All') {
            filtered = filtered.filter(record => getActivityType(record.path) === selectedActivityType);
        }

        // Filter by search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(record => {
                const className = (record.ClassName || '').toLowerCase();
                const role = (record.Role || '').toLowerCase();
                const date = ((record.Date || record.date || '')).toString().toLowerCase();
                const skillName = (record.SkillName || '').toLowerCase();
                const partner = ((record.Partner || record.PracticePartner || '')).toLowerCase();
                const hours = ((record.Hours || record.PracticeHours || '')).toString().toLowerCase();
                
                return className.includes(query) || 
                       role.includes(query) || 
                       date.includes(query) ||
                       skillName.includes(query) ||
                       partner.includes(query) ||
                       hours.includes(query);
            });
        }

        // Sort records
        filtered.sort((a, b) => {
            let aValue, bValue;
            
            switch(sortBy) {
                case 'date':
                    aValue = a.Date || a.date || '';
                    bValue = b.Date || b.date || '';
                    // Try to parse as YYYYMMDD
                    if (aValue.length === 8 && /^\d+$/.test(aValue)) {
                        aValue = parseInt(aValue);
                    }
                    if (bValue.length === 8 && /^\d+$/.test(bValue)) {
                        bValue = parseInt(bValue);
                    }
                    break;
                case 'type':
                    aValue = getActivityType(a.path);
                    bValue = getActivityType(b.path);
                    break;
                case 'name':
                    aValue = (a.ClassName || a.Role || '').toLowerCase();
                    bValue = (b.ClassName || b.Role || '').toLowerCase();
                    break;
                default:
                    return 0;
            }

            if (sortOrder === 'asc') {
                return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
            } else {
                return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
            }
        });

        return filtered;
    }, [records, selectedActivityType, searchQuery, sortBy, sortOrder]);

    // Get record counts by type
    const recordCounts = useMemo(() => {
        const counts = {};
        records.forEach(record => {
            const type = getActivityType(record.path);
            counts[type] = (counts[type] || 0) + 1;
        });
        counts['All'] = records.length;
        return counts;
    }, [records]);

    return (
        <div className="manage-activities">
            <h1 className="title">Manage Activities</h1>
            
            {notification.show && (
                <div className={`notification ${notification.type}`}>
                    {notification.message}
                </div>
            )}

            <div className="user-selection-section">
                <div className="select-container">
                    <label className="select-label">Select User</label>
                    <Select
                        className="user-select"
                        options={users}
                        onChange={handleUserChange}
                        placeholder="Search and select a user..."
                        isClearable
                        isSearchable
                    />
                </div>
            </div>

            {selectedUser && (
                <>
                    <div className="stats-section">
                        <div className="stat-card">
                            <div className="stat-value">{records.length}</div>
                            <div className="stat-label">Total Records</div>
                        </div>
                        {Object.entries(recordCounts).filter(([type]) => type !== 'All').map(([type, count]) => (
                            <div key={type} className="stat-card">
                                <div className="stat-value">{count}</div>
                                <div className="stat-label">{getActivityTypeName(type)}</div>
                            </div>
                        ))}
                    </div>

                    <div className="filters-section">
                        <div className="search-container">
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Search records..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button className="clear-search" onClick={() => setSearchQuery('')}>×</button>
                            )}
                        </div>

                        <div className="sort-container">
                            <label className="sort-label">Sort by:</label>
                            <select 
                                className="sort-select" 
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                            >
                                <option value="date">Date</option>
                                <option value="type">Activity Type</option>
                                <option value="name">Name</option>
                            </select>
                            <button 
                                className="sort-order-button"
                                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                                title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                            >
                                {sortOrder === 'asc' ? '↑' : '↓'}
                            </button>
                        </div>
                    </div>

                    <div className="activity-tabs">
                        {activityTypes.map(type => (
                            <button
                                key={type}
                                className={`activity-tab ${selectedActivityType === type ? 'active' : ''}`}
                                onClick={() => setSelectedActivityType(type)}
                            >
                                {type === 'All' ? 'All Activities' : getActivityTypeName(type)}
                                {type !== 'All' && <span className="tab-count">({recordCounts[type] || 0})</span>}
                            </button>
                        ))}
                    </div>

                    <div className="records-container">
                        {isLoading && <div className="loading-spinner" />}
                        
                        {filteredAndSortedRecords.length > 0 ? (
                            <div className="records-grid">
                                {Array.from(new Set(filteredAndSortedRecords.map(record => getActivityType(record.path)))).map(activityType => {
                                    const typeRecords = filteredAndSortedRecords.filter(record => getActivityType(record.path) === activityType);
                                    return (
                                        <div key={activityType} className="activity-section">
                                            <div className="activity-header">
                                                <h2 className="activity-title">{getActivityTypeName(activityType)}</h2>
                                                <span className="activity-count">{typeRecords.length} record{typeRecords.length !== 1 ? 's' : ''}</span>
                                            </div>
                                            <div className="table-responsive">
                                                <table className="records-table">
                                                    <thead>
                                                        <tr>
                                                            {activityType === 'ClassData' && <th>Class</th>}
                                                            {activityType !== 'ClassData' && activityType !== 'SkillData' && <th>Role</th>}
                                                            <th>Date</th>
                                                            {activityType === 'SkillData' && (
                                                                <>
                                                                    <th>Hours</th>
                                                                    <th>Partner</th>
                                                                    <th>Skill Name</th>
                                                                </>
                                                            )}
                                                            <th className="actions-header">Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {typeRecords.map(record => (
                                                            <tr key={record.id}>
                                                                {activityType === 'ClassData' && (
                                                                    <td>
                                                                        {editingRecords[record.id] ? (
                                                                            <input 
                                                                                className="input-field" 
                                                                                type="text" 
                                                                                value={record.ClassName || ''} 
                                                                                onChange={(e) => handleInputChange(record.id, 'ClassName', e.target.value)} 
                                                                            />
                                                                        ) : (
                                                                            <span className="record-value">
                                                                                {record.ClassName || 'N/A'}
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                )}
                                                                {activityType !== 'ClassData' && activityType !== 'SkillData' && (
                                                                    <td>
                                                                        {editingRecords[record.id] ? (
                                                                            <input 
                                                                                className="input-field" 
                                                                                type="text" 
                                                                                value={record.Role || ''} 
                                                                                onChange={(e) => handleInputChange(record.id, 'Role', e.target.value)} 
                                                                            />
                                                                        ) : (
                                                                            <span className="record-value">
                                                                                {record.Role || 'N/A'}
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                )}
                                                                <td>
                                                                    {editingRecords[record.id] ? (
                                                                        <input 
                                                                            className="input-field" 
                                                                            type="text" 
                                                                            value={record.Date || record.date || ''} 
                                                                            onChange={(e) => handleInputChange(record.id, 'Date', e.target.value)} 
                                                                        />
                                                                    ) : (
                                                                        <span className="record-value date-value">
                                                                            {formatDate(record.Date || record.date)}
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                {editingRecords[record.id] ? (
                                                                    <>
                                                                        {activityType === 'SkillData' && (
                                                                            <>
                                                                                <td>
                                                                                    <input className="input-field" type="text" value={record.PracticeHours || ''} onChange={(e) => handleInputChange(record.id, 'PracticeHours', e.target.value)} />
                                                                                </td>
                                                                                <td>
                                                                                    <input className="input-field" type="text" value={record.PracticePartner || ''} onChange={(e) => handleInputChange(record.id, 'PracticePartner', e.target.value)} />
                                                                                </td>
                                                                                <td>
                                                                                    <input className="input-field" type="text" value={record.SkillName || ''} onChange={(e) => handleInputChange(record.id, 'SkillName', e.target.value)} />
                                                                                </td>
                                                                            </>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        {activityType === 'SkillData' && (
                                                                            <>
                                                                                <td><span className="record-value">{record.PracticeHours || 'N/A'}</span></td>
                                                                                <td><span className="record-value">{record.PracticePartner !== undefined ? record.PracticePartner : 'N/A'}</span></td>
                                                                                <td><span className="record-value">{record.SkillName || 'N/A'}</span></td>
                                                                            </>
                                                                        )}
                                                                    </>
                                                                )}
                                                                <td className="actions-cell">
                                                                    <div className="action-buttons">
                                                                        {editingRecords[record.id] ? (
                                                                            <button className="action-button save-button" onClick={() => handleSaveEdit(record.id, record)}>Save</button>
                                                                        ) : (
                                                                            <button className="action-button edit-button" onClick={() => handleEdit(record.id)}>Edit</button>
                                                                        )}
                                                                        <button className="action-button delete-button" onClick={() => handleDelete(record.id)}>Delete</button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="no-records">
                                <p>{searchQuery || selectedActivityType !== 'All' ? 'No records match your filters.' : 'No records found for the selected user.'}</p>
                                {(searchQuery || selectedActivityType !== 'All') && (
                                    <button className="clear-filters-button" onClick={() => {
                                        setSearchQuery('');
                                        setSelectedActivityType('All');
                                    }}>
                                        Clear Filters
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}

            {!selectedUser && !isLoading && (
                <div className="no-user-selected">
                    <p>Please select a user to view their activities.</p>
                </div>
            )}
        </div>
    );
}

export default ManageActivities;
